// Live's export settings (spec E2, E2.2, #243): the Download popover's presets and its Custom
// view, as data. Pure: the popover shows them, the exporter makes them, the tests read them.

/** A video codec, by live's name for it. */
export type VideoCodecName = "avc" | "hevc" | "vp9";
/** An audio codec; "pcm" is WAV's. */
export type AudioCodecName = "aac" | "opus" | "mp3" | "flac" | "pcm";
/** A file's container, by its extension. */
export type Container = "mp4" | "webm" | "mkv" | "mov" | "mp3" | "wav" | "flac" | "ogg";

export interface VideoSettings {
  /** Pixels on the frame's short side: 720, 1080, 1440 or 2160. */
  short: number;
  codec: VideoCodecName;
  /** Bits a second. */
  bitrate: number;
  fps: number;
}

export interface AudioSettings {
  codec: AudioCodecName;
  sampleRate: 44_100 | 48_000;
}

/** What an export makes: its picture (none for a sound file), its sound (none for a silent video), its container. */
export interface ExportSettings {
  video: VideoSettings | null;
  audio: AudioSettings | null;
  container: Container;
}

export interface Preset {
  name: string;
  /** As the row shows it: "720p". */
  resolution: string;
  /** As the row shows it: "MP4". */
  format: "MP4" | "WEBM";
  settings: ExportSettings;
}

const mp4 = (short: number, codec: VideoCodecName, bitrate: number): ExportSettings => ({
  video: { short, codec, bitrate, fps: 30 },
  audio: { codec: "aac", sampleRate: 48_000 },
  container: "mp4",
});

const webm = (short: number, bitrate: number): ExportSettings => ({
  video: { short, codec: "vp9", bitrate, fps: 30 },
  audio: { codec: "opus", sampleRate: 48_000 },
  container: "webm",
});

/** Live's presets, in its groups and order, with its codecs and bitrates (`export-popover.tsx`). */
export const PRESET_GROUPS: readonly { label: string; presets: readonly Preset[] }[] = [
  {
    label: "Standard",
    presets: [
      { name: "HD", resolution: "720p", format: "MP4", settings: mp4(720, "avc", 7_000_000) },
      { name: "Full HD", resolution: "1080p", format: "MP4", settings: mp4(1080, "avc", 12_000_000) },
      { name: "2K Quad HD", resolution: "1440p", format: "MP4", settings: mp4(1440, "vp9", 24_000_000) },
      { name: "4K Ultra HD", resolution: "2160p", format: "MP4", settings: mp4(2160, "vp9", 64_000_000) },
    ],
  },
  {
    label: "Social Media",
    presets: [
      { name: "YouTube Shorts", resolution: "1080p", format: "MP4", settings: mp4(1080, "avc", 12_000_000) },
      { name: "YouTube 4K", resolution: "2160p", format: "MP4", settings: mp4(2160, "vp9", 64_000_000) },
      { name: "Instagram Reels", resolution: "1080p", format: "MP4", settings: mp4(1080, "avc", 12_000_000) },
      { name: "TikTok", resolution: "1080p", format: "MP4", settings: mp4(1080, "avc", 12_000_000) },
    ],
  },
  {
    label: "Web",
    presets: [
      { name: "HD", resolution: "720p", format: "WEBM", settings: webm(720, 5_000_000) },
      { name: "Full HD", resolution: "1080p", format: "WEBM", settings: webm(1080, 8_000_000) },
    ],
  },
];

/** A sound's bitrate where its codec takes one (WAV's and FLAC's do not), bits a second. */
export const AUDIO_BITRATE = 192_000;

/**
 * The most an export made in memory may weigh (spec E2, decisions 24 and 28): a browser without
 * its own file system (OPFS) makes the file in memory (`BufferTarget`), and a tab holds about this
 * much before it fails. Where OPFS streams it to disk, the bound is the storage's room instead.
 */
export const MAX_EXPORT_BYTES = 1_500_000_000;

/** Where an export is made, for its size bound: in memory, or on disk with this much room. */
export type ExportRoom = { kind: "memory" } | { kind: "disk"; bytes: number };

/** About how many bytes the settings make of `durationMs`: the bitrates times the length. */
export function estimatedBytes(settings: ExportSettings, durationMs: number): number {
  const bitsPerSecond = (settings.video?.bitrate ?? 0) + (settings.audio === null ? 0 : AUDIO_BITRATE);
  return (bitsPerSecond * durationMs) / 1000 / 8;
}

/**
 * Why an export of `durationMs` is refused for its size, naming its resolution, or null. Every
 * export is estimated. In memory, a preset's 4K passes the bound after about three minutes and
 * Custom's bitrate (up to 200 Mbps) can pass it at any size; on disk, only the storage's room
 * bounds it.
 */
export function sizeRefusalOf(
  settings: ExportSettings,
  durationMs: number,
  room: ExportRoom = { kind: "memory" },
): string | null {
  const bytes = estimatedBytes(settings, durationMs);
  const most = room.kind === "memory" ? MAX_EXPORT_BYTES : room.bytes;
  if (bytes <= most) return null;
  const gb = (value: number) => (value / 1e9).toFixed(1);
  const what = settings.video === null ? "This sound file" : `A ${settings.video.short}p export of this video`;
  const where =
    room.kind === "memory"
      ? `more than this browser can hold (${gb(most)} GB)`
      : `more than this browser's storage has room for (${gb(most)} GB)`;
  return `${what} would be about ${gb(bytes)} GB, ${where}. Shorten the video or choose a smaller size or bitrate.`;
}

/** The server's render makes this one preset (E1), where the browser cannot make any. */
export const SERVER_PRESET: ExportSettings = mp4(720, "avc", 7_000_000);

/** Live's Custom resolutions, each with the bitrate a choice resets to. */
export const RESOLUTIONS: readonly { label: string; short: number; bitrate: number }[] = [
  { label: "720p", short: 720, bitrate: 7_000_000 },
  { label: "1080p", short: 1080, bitrate: 12_000_000 },
  { label: "1440p", short: 1440, bitrate: 24_000_000 },
  { label: "2160p", short: 2160, bitrate: 64_000_000 },
];

export const VIDEO_CODECS: readonly { value: VideoCodecName; label: string }[] = [
  { value: "avc", label: "H.264 (AVC)" },
  { value: "hevc", label: "H.265 (HEVC)" },
  { value: "vp9", label: "VP9" },
];

export const AUDIO_CODECS: readonly { value: Exclude<AudioCodecName, "pcm">; label: string }[] = [
  { value: "aac", label: "AAC" },
  { value: "opus", label: "Opus" },
  { value: "mp3", label: "MP3" },
  { value: "flac", label: "FLAC" },
];

/** A video file's containers, each with the video codecs it takes (live's lists). */
export const VIDEO_FORMATS: readonly { value: Container; label: string; codecs: readonly VideoCodecName[] }[] = [
  { value: "mp4", label: "MP4", codecs: ["avc", "hevc", "vp9"] },
  { value: "webm", label: "WebM", codecs: ["vp9"] },
  { value: "mkv", label: "MKV", codecs: ["avc", "hevc", "vp9"] },
  { value: "mov", label: "MOV", codecs: ["avc", "hevc"] },
];

/** A sound file's containers, with Video off (live's), each with the codec it is written in. */
export const AUDIO_FORMATS: readonly { value: Container; label: string; codec: AudioCodecName }[] = [
  { value: "mp3", label: "MP3", codec: "mp3" },
  { value: "wav", label: "WAV", codec: "pcm" },
  { value: "flac", label: "FLAC", codec: "flac" },
  { value: "ogg", label: "OGG", codec: "opus" },
];

/** Live's frame rates, in live's order (15 last). */
export const FRAME_RATES: readonly { value: number; label: string }[] = [
  { value: 23.976, label: "23.976 fps (Film)" },
  { value: 24, label: "24 fps" },
  { value: 25, label: "25 fps (PAL)" },
  { value: 29.97, label: "29.97 fps (NTSC)" },
  { value: 30, label: "30 fps" },
  { value: 50, label: "50 fps" },
  { value: 59.94, label: "59.94 fps (NTSC)" },
  { value: 60, label: "60 fps" },
  { value: 15, label: "15 fps" },
];

/** Live's Custom view as it opens: 1080p, 12 Mbps, H.264, MP4, 30 fps, AAC at 48 kHz. */
export const CUSTOM_DEFAULT: ExportSettings = mp4(1080, "avc", 12_000_000);

/**
 * Custom's settings kept consistent as live's view keeps them: a container takes only its codecs
 * (the first when the chosen one is not), WebM's sound is Opus and any other's is not, and a
 * sound file's codec is its container's.
 */
export function consistent(settings: ExportSettings): ExportSettings {
  if (settings.video === null) {
    const format = AUDIO_FORMATS.find((each) => each.value === settings.container) ?? AUDIO_FORMATS[0]!;
    const sampleRate = settings.audio?.sampleRate ?? 48_000;
    return { video: null, audio: { codec: format.codec, sampleRate }, container: format.value };
  }
  const format = VIDEO_FORMATS.find((each) => each.value === settings.container) ?? VIDEO_FORMATS[0]!;
  const codec = format.codecs.includes(settings.video.codec) ? settings.video.codec : format.codecs[0]!;
  const audio =
    settings.audio === null
      ? null
      : {
          ...settings.audio,
          codec:
            format.value === "webm"
              ? ("opus" as const)
              : settings.audio.codec === "opus" || settings.audio.codec === "pcm"
                ? ("aac" as const)
                : settings.audio.codec,
        };
  return { video: { ...settings.video, codec }, audio, container: format.value };
}

/** The saved file's extension. */
export function extensionOf(settings: ExportSettings): string {
  return settings.container;
}

/** A setting's words, for a refusal: "H.265 (HEVC) video", "MP3 audio". */
export function codecWords(kind: "video" | "audio", codec: VideoCodecName | AudioCodecName): string {
  const label =
    kind === "video"
      ? VIDEO_CODECS.find((each) => each.value === codec)?.label
      : codec === "pcm"
        ? "WAV"
        : AUDIO_CODECS.find((each) => each.value === codec)?.label;
  return `${label ?? codec} ${kind}`;
}
